import { Lang } from '@src/i18n/lang';

/**
 * One v2 build serves both sites, so the site is resolved at runtime from the
 * hostname. santa-frontend bakes `window.__ssSite` into each of its two builds
 * instead; honour it when present so PR previews served at
 * com-preview-<slug>.santa-secret.ru behave as .com here too.
 */
export function isDotCom(): boolean {
  if (import.meta.env.DEV) {
    const requested = new URLSearchParams(window.location.search).get('site');
    if (requested === 'com' || requested === 'ru') {
      document.cookie = `ss_dev_site=${requested};path=/;max-age=604800;SameSite=Lax`;
      return requested === 'com';
    }
    const match = /(?:^|;\s*)ss_dev_site=(com|ru)(?:;|$)/.exec(document.cookie);
    if (match) return match[1] === 'com';
  }
  const baked = (window as unknown as { __ssSite?: string }).__ssSite;
  if (baked) {
    return baked === 'com';
  }
  return window.location.hostname.endsWith('santa-secret.com');
}

/** Support inbox for this site. .com never shows the .ru address. */
export function supportEmail(): string {
  return isDotCom() ? 'postman@santa-secret.com' : 'postman@santa-secret.ru';
}

const LANG_COOKIE_RE = /(?:^|;\s*)lang=(ru|en)(?:;|$)/i;

function langFromCookie(): Lang | null {
  const match = LANG_COOKIE_RE.exec(document.cookie);
  if (!match) {
    return null;
  }
  return match[1].toLowerCase() === Lang.EN ? Lang.EN : Lang.RU;
}

/**
 * .com is always English. .ru defaults to Russian and honours the same `lang`
 * cookie santa-frontend writes, so a switch on either build sticks.
 */
export function siteLanguage(): Lang {
  if (isDotCom()) {
    return Lang.EN;
  }
  return langFromCookie() ?? Lang.RU;
}

/** Persist a .ru language choice. No-op on .com — that site stays English. */
export function setSiteLanguage(lang: Lang): void {
  if (isDotCom()) {
    return;
  }
  document.cookie = `lang=${lang};path=/;max-age=31536000;samesite=lax`;
}
